'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageListRoot, useChatDensity } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageListUtilityClasses } from "./chatMessageListClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageList');
const ChatMessageListStyled = styled('div', {
  name: 'MuiChatMessageList',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  backgroundColor: (theme.vars || theme).palette.background.default
}));
const ChatMessageListScrollerStyled = styled('div', {
  name: 'MuiChatMessageList',
  slot: 'Scroller',
  overridesResolver: (_, styles) => styles.scroller
})(() => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  minHeight: 0
}));
const ChatMessageListContentStyled = styled('div', {
  name: 'MuiChatMessageList',
  slot: 'Content',
  overridesResolver: (_, styles) => styles.content
})(({
  theme,
  ownerState
}) => {
  const densityPaddingBlock = {
    compact: theme.spacing(0.5),
    standard: theme.spacing(1),
    comfortable: theme.spacing(1.5)
  };
  return {
    display: 'flex',
    flexDirection: 'column',
    paddingBlock: densityPaddingBlock[ownerState?.density ?? 'standard'],
    boxSizing: 'border-box'
  };
});
const ChatMessageList = /*#__PURE__*/React.forwardRef(function ChatMessageList(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageList'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatMessageListUtilityClasses(classesProp);
  const density = useChatDensity();
  return /*#__PURE__*/_jsx(MessageListRoot, _extends({
    ref: ref
  }, other, {
    slots: _extends({
      messageList: slots?.messageList ?? ChatMessageListStyled,
      messageListScroller: slots?.messageListScroller ?? ChatMessageListScrollerStyled,
      messageListContent: slots?.messageListContent ?? ChatMessageListContentStyled
    }, slots),
    slotProps: _extends({}, slotProps, {
      messageList: _extends({
        className: clsx(classes.root, className),
        sx
      }, slotProps?.messageList),
      messageListScroller: _extends({
        className: classes.scroller
      }, slotProps?.messageListScroller),
      messageListContent: _extends({
        className: classes.content,
        ownerState: {
          density
        }
      }, slotProps?.messageListContent)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageList.displayName = "ChatMessageList";
process.env.NODE_ENV !== "production" ? ChatMessageList.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * Controls automatic scrolling to the bottom when new messages arrive or
   * streaming content grows, as long as the user is within `buffer` pixels of
   * the bottom.
   *
   * - `true` – enable with the default buffer (150 px).
   * - `{ buffer: number }` – enable with a custom threshold.
   * - `false` – disable (the scroll-to-bottom affordance is still available).
   *
   * Scrolling when the *user* sends a message is always active.
   * @default true
   */
  autoScroll: PropTypes.oneOfType([PropTypes.shape({
    buffer: PropTypes.number
  }), PropTypes.bool]),
  classes: PropTypes.object,
  className: PropTypes.string,
  estimatedItemSize: PropTypes.number,
  getItemKey: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.string),
  onReachTop: PropTypes.func,
  overlay: PropTypes.node,
  renderItem: PropTypes.func.isRequired,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessageList };